import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { bloodTestsAPI } from '../services/api'
import HealthScoreCard from '../components/blood-tests/HealthScoreCard'
import RiskFactorsList from '../components/blood-tests/RiskFactorsList'
import RecommendationsList from '../components/blood-tests/RecommendationsList'
import MarkersByCategoryView from '../components/blood-tests/MarkersByCategoryView'

export default function BloodTestDashboard() {
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data: bloodTest, isLoading, error } = useQuery({
    queryKey: ['bloodTest', id],
    queryFn: async () => {
      const response = await bloodTestsAPI.getById(id)
      return response.data
    },
    refetchInterval: (data) => {
      // Poll every second if test is being processed
      const isProcessing = data?.status === 'processing' || data?.status === 'pending'
      return isProcessing ? 1000 : false
    },
    staleTime: 0,
  })

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blood test results...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <h3 className="text-danger-800 font-semibold">Error loading blood test</h3>
          <p className="text-danger-600 mt-1">{error.message}</p>
        </div>
      </div>
    )
  }

  const testDate = new Date(bloodTest.test_date).toLocaleDateString()
  const isAnalyzed = bloodTest.status === 'analyzed'

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/blood-tests" className="text-primary-600 hover:text-primary-700 text-sm mb-2 inline-block">
              ← Back to Blood Tests
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Blood Test Results</h1>
            <p className="text-gray-600 mt-1">
              {testDate} {bloodTest.lab_name && `• ${bloodTest.lab_name}`}
            </p>
          </div>
          <div className="flex gap-3">
            {!isAnalyzed && (bloodTest.status === 'uploaded' || bloodTest.status === 'failed') && (
              <button
                onClick={async () => {
                  await bloodTestsAPI.analyze(id)
                  queryClient.invalidateQueries({ queryKey: ['bloodTest', id] })
                }}
                className="btn-primary"
              >
                {bloodTest.status === 'failed' ? 'Retry AI Analysis' : 'Run AI Analysis'}
              </button>
            )}
            <Link to={`/blood-tests/${id}/trends`} className="btn-outline">
              View Trends
            </Link>
            <Link to={`/blood-tests/compare?current=${id}`} className="btn-outline">
              Compare Tests
            </Link>
          </div>
        </div>
      </div>

      {/* Status */}
      {bloodTest.status !== 'analyzed' && (
        <div className={`mb-6 border-l-4 p-4 rounded-r-md ${
          bloodTest.status === 'failed'
            ? 'bg-red-50 border-red-400'
            : 'bg-blue-50 border-blue-400'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className={`h-5 w-5 ${
                bloodTest.status === 'failed' ? 'text-red-400' : 'text-blue-400'
              }`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className={`text-sm ${
                bloodTest.status === 'failed' ? 'text-red-700' : 'text-blue-700'
              }`}>
                {bloodTest.status === 'processing' && 'This blood test is currently being processed...'}
                {bloodTest.status === 'uploaded' && 'This blood test has been uploaded but not yet analyzed. Click "Run AI Analysis" to get health insights.'}
                {bloodTest.status === 'failed' && (
                  <>
                    AI analysis failed: {bloodTest.processing_error || 'Unknown error'}. Click &quot;Retry AI Analysis&quot; to try again.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {isAnalyzed && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <HealthScoreCard
            score={bloodTest.health_score}
            bloodTestId={bloodTest.id}
            excludedCount={bloodTest.score_excluded_count || 0}
            contextCheckStatus={bloodTest.context_check_status}
          />

          <div className="card">
            <div className="card-body">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Abnormal Markers</h3>
              <div className="flex items-center justify-center">
                <div className={`text-4xl font-bold ${bloodTest.abnormal_markers_count > 0 ? 'text-warning-600' : 'text-success-600'}`}>
                  {bloodTest.abnormal_markers_count}
                </div>
              </div>
              {bloodTest.abnormal_markers_count > 0 && (
                <p className="text-center text-sm text-gray-600 mt-2">markers need attention</p>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Critical Markers</h3>
              <div className="flex items-center justify-center">
                <div className={`text-4xl font-bold ${bloodTest.critical_markers_count > 0 ? 'text-danger-600' : 'text-success-600'}`}>
                  {bloodTest.critical_markers_count}
                </div>
              </div>
              {bloodTest.critical_markers_count > 0 && (
                <p className="text-center text-sm text-danger-600 mt-2 font-medium">Immediate attention required</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Summary */}
      {bloodTest.ai_analysis_summary && (
        <div className="card mb-8">
          <div className="card-header">
            <h2 className="text-xl font-semibold flex items-center">
              <svg className="h-6 w-6 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              AI Analysis Summary
            </h2>
          </div>
          <div className="card-body">
            <p className="text-gray-700 whitespace-pre-line">{bloodTest.ai_analysis_summary}</p>
          </div>
        </div>
      )}

      {/* Risk Factors and Recommendations */}
      {isAnalyzed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {bloodTest.risk_factors && <RiskFactorsList factors={bloodTest.risk_factors} />}
          {bloodTest.recommendations && <RecommendationsList items={bloodTest.recommendations} />}
        </div>
      )}

      {/* Detailed Markers */}
      {bloodTest.test_results && bloodTest.test_results.length > 0 && (
        <MarkersByCategoryView results={bloodTest.test_results} bloodTestId={bloodTest.id} />
      )}

      {/* User Notes */}
      {bloodTest.user_notes && (
        <div className="card mt-8">
          <div className="card-header">
            <h2 className="text-xl font-semibold">Notes</h2>
          </div>
          <div className="card-body">
            <p className="text-gray-700">{bloodTest.user_notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}
